// SCR-STAFF-877 تب‌های مالی تأمین‌کنندگان (AP-INV-03؛ US-INV-061 تا 064): مانده و سررسیدها (معوق، ۷ و ۳۰ روز)، صورتحساب، پرداخت نقد، انتقال یا چک
// صیادی با تخصیص خودکار به فاکتورها (بیش از مانده فقط با پیش‌پرداخت — BR-INV-060)، وصول یا برگشت چک، قرارداد تأمین با قیمت و حداقل سفارش و تحلیل
// قیمت خرید (آخرین قیمت هر تأمین‌کننده، میانگین، کمینه و روند ماهانه).
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  unwrap,
  type InvDueItemView,
  type InvItemPriceAnalysisView,
  type InvItemUnit,
  type InvStatementRowView,
  type InvSupplierBalanceView,
  type InvSupplierChequeView,
  type InvSupplierPaymentMethod,
  type InvSupplierPaymentView,
  type InvSupplyContractView,
} from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, faDigits, FormDialog, JalaliDate, latinDigits, StatusChip, type DataTableColumn } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { WriteNotice } from '../settings/common';
import { parseJalaliInput } from '../patients/common';
import { chequeTone, MoneyCell, PAYMENT_METHODS, procurementKeys, ReasonDialog, rialsOf, SupplierSelect, unitsOf } from './procurementCommon';
import { toMilli, useItemOptions, useQuantityText } from './stockCommon';

/** مانده تأمین‌کنندگان، سررسیدها، صورتحساب و پرداخت (US-INV-061، 062). */
export function PayablesTab() {
  const { t } = useTranslation('inv');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const write = useWriteAccess('inv.supplier_payment.manage');
  const [paying, setPaying] = useState<string | null | undefined>(undefined);
  const [statementOf, setStatementOf] = useState<InvSupplierBalanceView | null>(null);
  const payables = useQuery({
    queryKey: procurementKeys.payables(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/inventory/payables')),
  });
  const balanceColumns: DataTableColumn<InvSupplierBalanceView>[] = [
    { id: 'supplier', headerKey: 'inv:payables.columns.supplier', cell: (row) => row.supplierName },
    { id: 'invoiced', headerKey: 'inv:payables.columns.invoiced', cell: (row) => <MoneyCell value={row.invoiced} /> },
    { id: 'paid', headerKey: 'inv:payables.columns.paid', cell: (row) => <MoneyCell value={row.paid} /> },
    { id: 'credits', headerKey: 'inv:payables.columns.credits', cell: (row) => <MoneyCell value={row.credits} /> },
    {
      id: 'balance',
      headerKey: 'inv:payables.columns.balance',
      cell: (row) => (
        <span data-testid={`payable-balance-${row.supplierId}`}>
          <MoneyCell value={row.balance} />
        </span>
      ),
    },
    { id: 'overdue', headerKey: 'inv:payables.columns.overdue', cell: (row) => <MoneyCell value={row.overdue} /> },
    { id: 'due7', headerKey: 'inv:payables.columns.due_in_7', cell: (row) => <MoneyCell value={row.dueIn7} /> },
    { id: 'due30', headerKey: 'inv:payables.columns.due_in_30', cell: (row) => <MoneyCell value={row.dueIn30} /> },
    { id: 'cheques', headerKey: 'inv:payables.columns.cheques', cell: (row) => <MoneyCell value={row.chequesOutstanding} /> },
    {
      id: 'actions',
      headerKey: 'inv:payables.columns.actions',
      cell: (row) => (
        <Stack direction="row" spacing={0.5}>
          <Button size="small" onClick={() => setStatementOf(row)} data-testid={`payable-statement-${row.supplierId}`}>
            {t('inv:payables.statement')}
          </Button>
          <Button size="small" variant="outlined" disabled={!write.canWrite} onClick={() => setPaying(row.supplierId)} data-testid={`payable-pay-${row.supplierId}`}>
            {t('inv:payables.pay')}
          </Button>
        </Stack>
      ),
    },
  ];
  const dueColumns: DataTableColumn<InvDueItemView>[] = [
    { id: 'kind', headerKey: 'inv:payables.columns.kind', cell: (row) => t(`inv:payables.due_kind.${row.kind}`) },
    { id: 'number', headerKey: 'inv:payables.columns.number', cell: (row) => <bdi dir="ltr">{row.number}</bdi> },
    { id: 'supplier', headerKey: 'inv:payables.columns.supplier', cell: (row) => row.supplierName },
    { id: 'due', headerKey: 'inv:payables.columns.due_date', cell: (row) => <JalaliDate value={row.dueDate} /> },
    { id: 'days', headerKey: 'inv:payables.columns.days_left', cell: (row) => faDigits(Number(row.daysLeft)) },
    { id: 'amount', headerKey: 'inv:payables.columns.amount', cell: (row) => <MoneyCell value={row.amount} /> },
  ];
  const data = payables.data;
  return (
    <Stack spacing={2}>
      <WriteNotice write={write} />
      <Stack direction="row">
        <Button variant="contained" startIcon={<AddIcon />} disabled={!write.canWrite} onClick={() => setPaying(null)} data-testid="payment-create">
          {t('inv:payables.new_payment')}
        </Button>
      </Stack>
      <DataTable
        captionKey="inv:payables.caption"
        columns={balanceColumns}
        rows={data?.suppliers}
        getRowId={(row) => row.supplierId}
        isLoading={payables.isPending}
        error={payables.error}
        onRetry={() => void payables.refetch()}
        emptyTitleKey="inv:payables.empty"
      />
      {data &&
        (['overdue', 'dueIn7', 'dueIn30'] as const).map((bucket) => (
          <Stack key={bucket} spacing={1}>
            <Typography variant="subtitle2" component="h3">
              {t(`inv:payables.buckets.${bucket}`)}
            </Typography>
            <DataTable captionKey={`inv:payables.buckets.${bucket}`} columns={dueColumns} rows={data[bucket]} getRowId={(row) => `${row.kind}-${row.id}`} isLoading={false} emptyTitleKey="inv:payables.no_dues" />
          </Stack>
        ))}
      {paying !== undefined && (
        <PaymentDialog
          supplierId={paying}
          onClose={() => setPaying(undefined)}
          onSaved={() => {
            setPaying(undefined);
            void queryClient.invalidateQueries({ queryKey: procurementKeys.payables(session?.tenantId) });
            void queryClient.invalidateQueries({ queryKey: procurementKeys.payments(session?.tenantId) });
            void queryClient.invalidateQueries({ queryKey: procurementKeys.cheques(session?.tenantId) });
          }}
        />
      )}
      {statementOf && <StatementDialog supplier={statementOf} onClose={() => setStatementOf(null)} />}
    </Stack>
  );
}

function StatementDialog({ supplier, onClose }: { supplier: InvSupplierBalanceView; onClose: () => void }) {
  const { t } = useTranslation('inv');
  const { tenantApi, session } = useTenantSession();
  const statement = useQuery({
    queryKey: [...procurementKeys.payables(session?.tenantId), 'statement', supplier.supplierId],
    queryFn: () => unwrap(tenantApi.GET('/api/v1/inventory/suppliers/{id}/statement', { params: { path: { id: supplier.supplierId } } })),
  });
  const columns: DataTableColumn<InvStatementRowView>[] = [
    { id: 'date', headerKey: 'inv:payables.columns.date', cell: (row) => <JalaliDate value={row.date} /> },
    { id: 'kind', headerKey: 'inv:payables.columns.kind', cell: (row) => t(`inv:payables.statement_kind.${row.kind}`) },
    { id: 'number', headerKey: 'inv:payables.columns.number', cell: (row) => <bdi dir="ltr">{row.number}</bdi> },
    { id: 'reference', headerKey: 'inv:payables.columns.reference', cell: (row) => <bdi dir="ltr">{row.reference ?? '—'}</bdi> },
    { id: 'debit', headerKey: 'inv:payables.columns.debit', cell: (row) => <MoneyCell value={row.debit} /> },
    { id: 'credit', headerKey: 'inv:payables.columns.credit', cell: (row) => <MoneyCell value={row.credit} /> },
    { id: 'balance', headerKey: 'inv:payables.columns.balance', cell: (row) => <MoneyCell value={row.balance} /> },
  ];
  return (
    <FormDialog open screenId="SCR-STAFF-877-STATEMENT" titleKey="inv:payables.statement_title" onSubmit={onClose} onClose={onClose} isDirty={false} isSubmitting={false} submitKey="common:actions.close" maxWidth="lg">
      <Typography>{supplier.supplierName}</Typography>
      {statement.data && (
        <Typography variant="body2" data-testid="statement-balances">
          {t('inv:payables.opening')}: <MoneyCell value={statement.data.openingBalance} /> · {t('inv:payables.closing')}: <MoneyCell value={statement.data.closingBalance} />
        </Typography>
      )}
      <DataTable
        captionKey="inv:payables.statement_title"
        columns={columns}
        rows={statement.data?.rows}
        getRowId={(row) => `${row.kind}-${row.documentId}`}
        isLoading={statement.isPending}
        error={statement.error}
        emptyTitleKey="inv:payables.statement_empty"
      />
    </FormDialog>
  );
}

function PaymentDialog({ supplierId: initialSupplier, onClose, onSaved }: { supplierId: string | null; onClose: () => void; onSaved: () => void }) {
  const { t } = useTranslation('inv');
  const { tenantApi } = useTenantSession();
  const access = useAccess();
  const idempotency = useIdempotencyKey();
  const [supplierId, setSupplierId] = useState(initialSupplier ?? '');
  const [method, setMethod] = useState<InvSupplierPaymentMethod>('BankTransfer');
  const [amount, setAmount] = useState('');
  const [paidOn, setPaidOn] = useState('');
  const [reference, setReference] = useState('');
  const [prepayment, setPrepayment] = useState(false);
  const [sayadId, setSayadId] = useState('');
  const [serial, setSerial] = useState('');
  const [bank, setBank] = useState('');
  const [chequeDue, setChequeDue] = useState('');
  const branchId = access.activeBranchId ?? access.branchIds[0] ?? '';
  const save = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/inventory/supplier-payments', {
          body: {
            supplierId,
            branchId,
            method,
            amount: { amount: rialsOf(amount) ?? 0, currency: 'IRR' },
            isPrepayment: prepayment,
            paidOn: parseJalaliInput(paidOn) ?? null,
            reference: reference.trim() || null,
            cashRegisterId: null,
            note: null,
            allocations: null,
            cheque:
              method === 'Cheque'
                ? { sayadId: latinDigits(sayadId.trim()), serialNumber: serial.trim() || null, bankName: bank.trim() || null, dueDate: parseJalaliInput(chequeDue) ?? '' }
                : null,
          },
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      ),
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: onSaved,
  });
  return (
    <FormDialog
      open
      screenId="SCR-STAFF-877-PAYMENT-FORM"
      titleKey="inv:payables.payment_title"
      onSubmit={() => (supplierId && rialsOf(amount) ? save.mutate() : undefined)}
      onClose={onClose}
      isDirty={Boolean(amount)}
      isSubmitting={save.isPending}
      submitError={save.error}
      maxWidth="md"
    >
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <SupplierSelect value={supplierId} onChange={setSupplierId} testId="payment-supplier" required />
        <TextField select size="small" label={t('inv:payables.method')} value={method} onChange={(event) => setMethod(event.target.value as InvSupplierPaymentMethod)} sx={{ minWidth: 180 }} slotProps={{ htmlInput: { 'data-testid': 'payment-method' } }}>
          {PAYMENT_METHODS.map((value) => (
            <MenuItem key={value} value={value}>
              {t(`inv:payment_method.${value}`)}
            </MenuItem>
          ))}
        </TextField>
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField size="small" required label={t('inv:payables.amount')} value={amount} onChange={(event) => setAmount(event.target.value)} slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'numeric', 'data-testid': 'payment-amount' } }} />
        <TextField
          size="small"
          label={t('inv:payables.paid_on')}
          placeholder="1405/07/13"
          value={paidOn}
          onChange={(event) => setPaidOn(event.target.value)}
          error={Boolean(paidOn.trim()) && !parseJalaliInput(paidOn)}
          slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'numeric' } }}
        />
        <TextField size="small" label={t('inv:payables.reference')} value={reference} onChange={(event) => setReference(event.target.value)} slotProps={{ htmlInput: { dir: 'ltr' } }} />
      </Stack>
      {method === 'Cheque' && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField size="small" required label={t('inv:payables.sayad_id')} helperText={t('inv:payables.sayad_help')} value={sayadId} onChange={(event) => setSayadId(event.target.value)} slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'numeric', 'data-testid': 'payment-sayad' } }} />
          <TextField
            size="small"
            required
            label={t('inv:payables.cheque_due')}
            placeholder="1405/08/13"
            value={chequeDue}
            onChange={(event) => setChequeDue(event.target.value)}
            error={Boolean(chequeDue.trim()) && !parseJalaliInput(chequeDue)}
            slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'numeric', 'data-testid': 'payment-cheque-due' } }}
          />
          <TextField size="small" label={t('inv:payables.serial')} value={serial} onChange={(event) => setSerial(event.target.value)} slotProps={{ htmlInput: { dir: 'ltr' } }} />
          <TextField size="small" label={t('inv:payables.bank')} value={bank} onChange={(event) => setBank(event.target.value)} />
        </Stack>
      )}
      <FormControlLabel control={<Checkbox checked={prepayment} onChange={(event) => setPrepayment(event.target.checked)} slotProps={{ input: { 'data-testid': 'payment-prepayment' } as object }} />} label={t('inv:payables.prepayment')} />
      <Alert severity="info">{t('inv:payables.allocation_note')}</Alert>
    </FormDialog>
  );
}

/** پرداخت‌ها و چک‌های صادره (US-INV-062). */
export function PaymentsTab() {
  const { t } = useTranslation('inv');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const write = useWriteAccess('inv.supplier_payment.manage');
  const [bouncing, setBouncing] = useState<InvSupplierChequeView | null>(null);
  const payments = useQuery({
    queryKey: procurementKeys.payments(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/inventory/supplier-payments', { params: { query: { limit: 50 } } })),
  });
  const cheques = useQuery({
    queryKey: procurementKeys.cheques(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/inventory/supplier-cheques')),
  });
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: procurementKeys.cheques(session?.tenantId) });
    void queryClient.invalidateQueries({ queryKey: procurementKeys.payables(session?.tenantId) });
  };
  const clear = useMutation({
    mutationFn: (cheque: InvSupplierChequeView) =>
      unwrap(tenantApi.POST('/api/v1/inventory/supplier-cheques/{id}:clear', { params: { path: { id: cheque.id } }, body: { clearedOn: null }, headers: { 'Idempotency-Key': idempotency.current() } })),
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: refresh,
  });
  const paymentColumns: DataTableColumn<InvSupplierPaymentView>[] = [
    { id: 'number', headerKey: 'inv:payables.columns.number', cell: (row) => <bdi dir="ltr" data-testid={`payment-${row.number}`}>{row.number}</bdi> },
    { id: 'supplier', headerKey: 'inv:payables.columns.supplier', cell: (row) => row.supplierName },
    { id: 'method', headerKey: 'inv:payables.method', cell: (row) => `${t(`inv:payment_method.${row.method}`)}${row.isPrepayment ? ` · ${t('inv:payables.prepayment_short')}` : ''}` },
    { id: 'amount', headerKey: 'inv:payables.columns.amount', cell: (row) => <MoneyCell value={row.amount} /> },
    {
      id: 'allocations',
      headerKey: 'inv:payables.columns.allocations',
      cell: (row) => (row.allocations.length ? row.allocations.map((allocation) => allocation.invoiceNumber).join(t('common:list_separator')) : '—'),
    },
    { id: 'date', headerKey: 'inv:payables.columns.date', cell: (row) => <JalaliDate value={row.paidOn} /> },
  ];
  const chequeColumns: DataTableColumn<InvSupplierChequeView>[] = [
    { id: 'sayad', headerKey: 'inv:payables.sayad_id', cell: (row) => <bdi dir="ltr">{row.sayadId}</bdi> },
    { id: 'supplier', headerKey: 'inv:payables.columns.supplier', cell: (row) => row.supplierName },
    { id: 'amount', headerKey: 'inv:payables.columns.amount', cell: (row) => <MoneyCell value={row.amount} /> },
    { id: 'due', headerKey: 'inv:payables.columns.due_date', cell: (row) => <JalaliDate value={row.dueDate} /> },
    {
      id: 'status',
      headerKey: 'inv:payables.columns.status',
      cell: (row) => (
        <span data-testid={`cheque-status-${row.sayadId}`}>
          <StatusChip tone={chequeTone(row.status)} labelKey={`inv:cheque_status.${row.status}`} />
        </span>
      ),
    },
    { id: 'sayadStatus', headerKey: 'inv:payables.columns.sayad_status', cell: (row) => t(`inv:sayad_status.${row.sayadStatus}`) },
    {
      id: 'actions',
      headerKey: 'inv:payables.columns.actions',
      cell: (row) =>
        row.status === 'Issued' ? (
          <Stack direction="row" spacing={0.5}>
            <Button size="small" disabled={!write.canWrite || clear.isPending} onClick={() => clear.mutate(row)} data-testid={`cheque-clear-${row.sayadId}`}>
              {t('inv:payables.clear')}
            </Button>
            <Button size="small" color="error" disabled={!write.canWrite} onClick={() => setBouncing(row)} data-testid={`cheque-bounce-${row.sayadId}`}>
              {t('inv:payables.bounce')}
            </Button>
          </Stack>
        ) : null,
    },
  ];
  return (
    <Stack spacing={2}>
      <WriteNotice write={write} />
      {clear.error && <Alert severity="error">{t('inv:purchasing.action_failed')}</Alert>}
      <DataTable
        captionKey="inv:payables.payments_caption"
        columns={paymentColumns}
        rows={payments.data?.items}
        getRowId={(row) => row.id}
        isLoading={payments.isPending}
        error={payments.error}
        onRetry={() => void payments.refetch()}
        emptyTitleKey="inv:payables.payments_empty"
      />
      <Typography variant="subtitle2" component="h3">
        {t('inv:payables.cheques_caption')}
      </Typography>
      <DataTable
        captionKey="inv:payables.cheques_caption"
        columns={chequeColumns}
        rows={cheques.data?.items}
        getRowId={(row) => row.id}
        isLoading={cheques.isPending}
        error={cheques.error}
        onRetry={() => void cheques.refetch()}
        emptyTitleKey="inv:payables.cheques_empty"
      />
      {bouncing && (
        <ReasonDialog
          titleKey="inv:payables.bounce_title"
          screenId="SCR-STAFF-877-CHEQUE-BOUNCE"
          onClose={() => setBouncing(null)}
          onConfirm={async (reason, key) => {
            await unwrap(tenantApi.POST('/api/v1/inventory/supplier-cheques/{id}:bounce', { params: { path: { id: bouncing.id } }, body: { bouncedOn: null, reason }, headers: { 'Idempotency-Key': key } }));
            setBouncing(null);
            refresh();
          }}
        />
      )}
    </Stack>
  );
}

/** قراردادهای تأمین (US-INV-063). */
export function ContractsTab() {
  const { t } = useTranslation('inv');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const quantity = useQuantityText();
  const write = useWriteAccess('inv.supply_contract.manage');
  const [creating, setCreating] = useState(false);
  const contracts = useQuery({
    queryKey: procurementKeys.contracts(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/inventory/supply-contracts')),
  });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: procurementKeys.contracts(session?.tenantId) });
  const toggle = useMutation({
    mutationFn: (contract: InvSupplyContractView) => {
      const options = { params: { path: { id: contract.id } }, headers: { 'Idempotency-Key': idempotency.current() } };
      return unwrap(
        contract.isActive
          ? tenantApi.POST('/api/v1/inventory/supply-contracts/{id}:deactivate', options)
          : tenantApi.POST('/api/v1/inventory/supply-contracts/{id}:activate', options),
      );
    },
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: refresh,
  });
  const columns: DataTableColumn<InvSupplyContractView>[] = [
    { id: 'number', headerKey: 'inv:contracts.columns.number', cell: (row) => <bdi dir="ltr" data-testid={`contract-${row.number}`}>{row.number}</bdi> },
    { id: 'supplier', headerKey: 'inv:contracts.columns.supplier', cell: (row) => row.supplierName },
    {
      id: 'validity',
      headerKey: 'inv:contracts.columns.validity',
      cell: (row) => (
        <span>
          <JalaliDate value={row.validFrom} /> – <JalaliDate value={row.validTo} />
        </span>
      ),
    },
    {
      id: 'items',
      headerKey: 'inv:contracts.columns.items',
      cell: (row) =>
        row.items
          .map((item) => `${item.itemName} (${t(`inv:unit.${item.unit}`)})${item.minOrderQuantityMilli ? ` · ${t('inv:contracts.min_order')}: ${quantity(item.minOrderQuantityMilli, item.unit)}` : ''}`)
          .join(t('common:list_separator')),
    },
    { id: 'prices', headerKey: 'inv:contracts.columns.prices', cell: (row) => <Stack>{row.items.map((item) => <MoneyCell key={item.itemId} value={item.unitPrice} />)}</Stack> },
    { id: 'terms', headerKey: 'inv:contracts.columns.payment_terms', cell: (row) => (row.paymentTermDays === null || row.paymentTermDays === undefined ? '—' : t('inv:suppliers.days', { value: faDigits(Number(row.paymentTermDays)) })) },
    {
      id: 'status',
      headerKey: 'inv:contracts.columns.status',
      cell: (row) => <StatusChip tone={row.isActive && row.isValidToday ? 'active' : 'inactive'} labelKey={row.isActive ? (row.isValidToday ? 'inv:contracts.valid' : 'inv:contracts.not_in_period') : 'inv:status.inactive'} />,
    },
    {
      id: 'actions',
      headerKey: 'inv:contracts.columns.actions',
      cell: (row) => (
        <Button size="small" disabled={!write.canWrite || toggle.isPending} onClick={() => toggle.mutate(row)}>
          {t(row.isActive ? 'inv:suppliers.deactivate' : 'inv:suppliers.activate')}
        </Button>
      ),
    },
  ];
  return (
    <Stack spacing={2}>
      <WriteNotice write={write} />
      <Stack direction="row">
        <Button variant="contained" startIcon={<AddIcon />} disabled={!write.canWrite} onClick={() => setCreating(true)} data-testid="contract-create">
          {t('inv:contracts.add')}
        </Button>
      </Stack>
      <DataTable
        captionKey="inv:contracts.caption"
        columns={columns}
        rows={contracts.data?.items}
        getRowId={(row) => row.id}
        isLoading={contracts.isPending}
        error={contracts.error}
        onRetry={() => void contracts.refetch()}
        emptyTitleKey="inv:contracts.empty"
      />
      {creating && (
        <ContractDialog
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            refresh();
          }}
        />
      )}
    </Stack>
  );
}

interface ContractItemState {
  itemId: string;
  unit: InvItemUnit | '';
  unitPrice: string;
  minOrder: string;
}

const emptyContractItem: ContractItemState = { itemId: '', unit: '', unitPrice: '', minOrder: '' };

function ContractDialog({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const { t } = useTranslation('inv');
  const { tenantApi } = useTenantSession();
  const idempotency = useIdempotencyKey();
  const items = useItemOptions();
  const [supplierId, setSupplierId] = useState('');
  const [validFrom, setValidFrom] = useState('');
  const [validTo, setValidTo] = useState('');
  const [paymentTermDays, setPaymentTermDays] = useState('');
  const [reference, setReference] = useState('');
  const [rows, setRows] = useState<ContractItemState[]>([{ ...emptyContractItem }]);
  const itemOf = (id: string) => items.data?.items.find((item) => item.id === id);
  const update = (index: number, patch: Partial<ContractItemState>) => setRows((current) => current.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  const save = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/inventory/supply-contracts', {
          body: {
            supplierId,
            validFrom: parseJalaliInput(validFrom) ?? '',
            validTo: parseJalaliInput(validTo) ?? '',
            reference: reference.trim() || null,
            paymentTermDays: paymentTermDays.trim() ? Number(latinDigits(paymentTermDays.trim())) : null,
            paymentTerms: null,
            note: null,
            items: rows
              .filter((row) => row.itemId)
              .map((row) => ({
                itemId: row.itemId,
                unit: row.unit || itemOf(row.itemId)?.purchaseUnit || null,
                unitPrice: { amount: rialsOf(row.unitPrice) ?? 0, currency: 'IRR' },
                minOrderQuantityMilli: row.minOrder.trim() ? toMilli(row.minOrder) : null,
              })),
          },
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      ),
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: onSaved,
  });
  return (
    <FormDialog
      open
      screenId="SCR-STAFF-877-CONTRACT-FORM"
      titleKey="inv:contracts.create_title"
      onSubmit={() => save.mutate()}
      onClose={onClose}
      isDirty={rows.some((row) => row.itemId)}
      isSubmitting={save.isPending}
      submitError={save.error}
      maxWidth="lg"
    >
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <SupplierSelect value={supplierId} onChange={setSupplierId} testId="contract-supplier" required />
        <TextField
          size="small"
          required
          label={t('inv:contracts.valid_from')}
          placeholder="1405/07/01"
          value={validFrom}
          onChange={(event) => setValidFrom(event.target.value)}
          error={Boolean(validFrom.trim()) && !parseJalaliInput(validFrom)}
          slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'numeric', 'data-testid': 'contract-valid-from' } }}
        />
        <TextField
          size="small"
          required
          label={t('inv:contracts.valid_to')}
          placeholder="1405/12/29"
          value={validTo}
          onChange={(event) => setValidTo(event.target.value)}
          error={Boolean(validTo.trim()) && !parseJalaliInput(validTo)}
          slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'numeric', 'data-testid': 'contract-valid-to' } }}
        />
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField size="small" label={t('inv:contracts.payment_term_days')} value={paymentTermDays} onChange={(event) => setPaymentTermDays(event.target.value)} slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'numeric', 'data-testid': 'contract-term-days' } }} />
        <TextField size="small" label={t('inv:contracts.reference')} value={reference} onChange={(event) => setReference(event.target.value)} slotProps={{ htmlInput: { dir: 'ltr' } }} />
      </Stack>
      <Typography variant="subtitle2" component="h3">
        {t('inv:contracts.items')}
      </Typography>
      {rows.map((row, index) => {
        const item = itemOf(row.itemId);
        return (
          <Stack key={index} direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }}>
            <TextField select size="small" required label={t('inv:stock.columns.item')} value={row.itemId} onChange={(event) => update(index, { itemId: event.target.value, unit: '' })} sx={{ minWidth: 220 }} slotProps={{ htmlInput: { 'data-testid': `contract-item-${index}` } }}>
              {(items.data?.items ?? []).map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField select size="small" label={t('inv:documents.unit')} value={row.unit || item?.purchaseUnit || ''} onChange={(event) => update(index, { unit: event.target.value as InvItemUnit })} sx={{ width: 140 }} disabled={!item}>
              {unitsOf(item).map((unit) => (
                <MenuItem key={unit} value={unit}>
                  {t(`inv:unit.${unit}`)}
                </MenuItem>
              ))}
            </TextField>
            <TextField size="small" required label={t('inv:purchasing.unit_price')} value={row.unitPrice} onChange={(event) => update(index, { unitPrice: event.target.value })} slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'numeric', 'data-testid': `contract-price-${index}` } }} />
            <TextField size="small" label={t('inv:contracts.min_order')} value={row.minOrder} onChange={(event) => update(index, { minOrder: event.target.value })} sx={{ width: 140 }} slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'decimal', 'data-testid': `contract-min-${index}` } }} />
            <IconButton aria-label={t('inv:documents.remove_line')} onClick={() => setRows((current) => current.filter((_row, i) => i !== index))} disabled={rows.length === 1}>
              <DeleteOutlineIcon />
            </IconButton>
          </Stack>
        );
      })}
      <Stack direction="row">
        <Button startIcon={<AddIcon />} onClick={() => setRows((current) => [...current, { ...emptyContractItem }])}>
          {t('inv:documents.add_line')}
        </Button>
      </Stack>
    </FormDialog>
  );
}

/** تحلیل قیمت خرید (US-INV-064). */
export function PriceAnalysisTab() {
  const { t } = useTranslation('inv');
  const { tenantApi, session } = useTenantSession();
  const analysis = useQuery({
    queryKey: procurementKeys.analysis(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/inventory/purchase-price-analysis')),
  });
  const columns: DataTableColumn<InvItemPriceAnalysisView>[] = [
    { id: 'item', headerKey: 'inv:analysis.columns.item', cell: (row) => `${row.itemName} (${t(`inv:unit.${row.baseUnit}`)})` },
    {
      id: 'average',
      headerKey: 'inv:analysis.columns.average',
      cell: (row) => (
        <span data-testid={`analysis-average-${row.itemId}`}>
          <MoneyCell value={row.averagePrice} />
        </span>
      ),
    },
    { id: 'minimum', headerKey: 'inv:analysis.columns.minimum', cell: (row) => <MoneyCell value={row.minimumPrice} /> },
    {
      id: 'suppliers',
      headerKey: 'inv:analysis.columns.suppliers',
      cell: (row) => (
        <Stack spacing={0.25}>
          {row.suppliers.map((supplier) => (
            <span key={supplier.supplierId}>
              {supplier.supplierName}: <MoneyCell value={supplier.lastPrice} /> (<JalaliDate value={supplier.lastDate} />)
            </span>
          ))}
        </Stack>
      ),
    },
    {
      id: 'trend',
      headerKey: 'inv:analysis.columns.trend',
      cell: (row) => (
        <Stack spacing={0.25}>
          {row.trend
            .filter((point) => point.averagePrice)
            .map((point) => (
              <span key={point.monthStart}>
                <JalaliDate value={point.monthStart} />: <MoneyCell value={point.averagePrice} />
              </span>
            ))}
        </Stack>
      ),
    },
  ];
  return (
    <Stack spacing={2}>
      <Alert severity="info">{t('inv:analysis.note')}</Alert>
      <DataTable
        captionKey="inv:analysis.caption"
        columns={columns}
        rows={analysis.data?.items}
        getRowId={(row) => row.itemId}
        isLoading={analysis.isPending}
        error={analysis.error}
        onRetry={() => void analysis.refetch()}
        emptyTitleKey="inv:analysis.empty"
      />
    </Stack>
  );
}
